import React from 'react';
import PropTypes from 'prop-types';
import { connectToStores } from 'fluxible-addons-react';
import { RouteStore, navigateAction } from 'fluxible-router';
import SearchResult from './SearchResult';
import GlossaryTermWidget from './GlossaryTermWidget';
import Spinner from '../../Spinner';
import Pagination from '../../Pagination';
import performSearchAction from '../../../action/performSearch';
import SearchService from '../../../services/SearchService';
import SearchStore, { SearchResultState } from '../../../stores/SearchStore';
import NavigationStore from '../../../stores/NavigationStore';
import styles from './styles.module.styl';

class SearchPage extends React.Component {
  constructor(props) {
    super(props);

    this.handleClick = this.handleClick.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
  }

  componentDidMount() {
    const { query, page } = this.props;

    if (query) {
      this.context.executeAction(performSearchAction, { query, page });
    }
  }

  handleClick({ e, id, url, isDocument }) {
    const { query } = this.props;

    SearchService.recordClickthrough(query, id, url);

    if (e.target.href) {
      e.preventDefault();

      if (isDocument) {
        this.context.executeAction(navigateAction, { url: new URL(e.target.href).pathname });
      } else {
        window.location.assign(e.target.href);
      }
    }
  }

  handlePageChange(page) {
    const { query } = this.props;

    this.context.executeAction(performSearchAction, { query, page });
  }

  renderResultContent() {
    const { result } = this.props;

    if (!result) {
      return <p>Please enter a query to search our documentation.</p>;
    }

    switch (result.state) {
    case SearchResultState.LOADING:
      return <Spinner />;

    case SearchResultState.LOADED:
      if (result.response.record_count === 0) {
        return <p>No results found. Would you like to try another search term?</p>;
      }

      // TODO: Show result count, pagination, etc.
      return result.response.records.page.map(page => (
        <SearchResult
          key={page.id}
          id={page.id}
          title={page.title}
          url={page.url}
          content={page.highlight.body}
          handleClick={this.handleClick}
        />
      ));


    case SearchResultState.ERROR:
      return <p>There was an error loading the search result. Please try again.</p>;

    default:
      throw new Error(`Unknown search state ${result.state}`);
    }
  }

  renderGlossaryTermWidget() {
    const { result, terms, query } = this.props;

    if (result && result.state === SearchResultState.LOADED && terms) {
      const matchedTerm = terms.find((term) => {
        if (term.title.toLowerCase() === query.toLowerCase()) {
          return true;
        }

        if (Array.isArray(term.alias) && term.alias.find(a => a.toLowerCase() === query.toLowerCase())) {
          return true;
        }

        return false;
      });

      if (matchedTerm) {
        return <GlossaryTermWidget {...matchedTerm} />;
      }
    }

    return <div style={{ display: 'none' }} />;
  }

  render() {
    const { result, page } = this.props;
    const title = result ? 'Search Results' : 'Search';

    return (
      <div className="document">
        <div className={`${styles.searchContainer} container search-results`}>
          <h1 className={styles.title}>{title}</h1>
          {this.renderGlossaryTermWidget()}
          <div className={styles.searchResults}>{this.renderResultContent()}</div>
          {
            result && result.state === SearchResultState.LOADED
            && <Pagination
              activePage={page}
              pageCount={result.response.info.page.num_pages}
              handleSelect={this.handlePageChange}
            />
          }
        </div>
      </div>
    );
  }
}

SearchPage.propTypes = {
  terms: PropTypes.object,
  result: PropTypes.object,
  query: PropTypes.string,
  page: PropTypes.string
};

SearchPage.contextTypes = {
  getStore: PropTypes.func.isRequired,
  executeAction: PropTypes.func.isRequired
};

export default connectToStores(SearchPage, [
  SearchStore,
  RouteStore,
  NavigationStore
], ({
  getStore
}) => {
  const { q: query, page } = getStore(RouteStore).getCurrentRoute().query;
  return {
    query,
    page,
    result: query ? getStore(SearchStore).getResult({ query, page }) : undefined,
    terms: getStore(NavigationStore).getGlossary().terms
  };
});
